import { BARIENDO_CT_SYSTEM, LocationType } from '@bariendo/lib/constants';
import { getReferenceString } from '@medplum/core';
import { Location } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useEffect, useState } from 'react';

export interface ProcedureLocationOption {
  /** What the navigator reads out and what the note records under `loc`. */
  name: string;
  /** `Location/<id>` — what availability is scoped to. */
  reference: string;
}

export interface ProcedureLocations {
  options: ProcedureLocationOption[];
  loading: boolean;
}

/**
 * The procedure sites, read from Orbit's `Location`s rather than listed in code.
 *
 * The card used to offer six marketing market names with a placeholder physician
 * each — none of which was an Orbit `Location`, so nothing downstream could use
 * the answer. A real reference is what lets availability be scoped to the site.
 */
export function useProcedureLocations(): ProcedureLocations {
  const medplum = useMedplum();
  const [options, setOptions] = useState<ProcedureLocationOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    medplum
      .searchResources('Location', {
        type: `${BARIENDO_CT_SYSTEM}|${LocationType.PROCEDURE_INSTITUTION}`,
        status: 'active',
        _sort: 'name',
        _count: 100,
      })
      .then((locations: Location[]) => {
        if (cancelled) return;
        setOptions(
          locations.flatMap((location) => {
            const reference = getReferenceString(location);
            return location.name && reference ? [{ name: location.name, reference }] : [];
          })
        );
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [medplum]);

  return { options, loading };
}
